/**
 * Indian Standard Time (IST, UTC+05:30) + 12-hour AM/PM formatting.
 *
 * The salon operates in Delhi, but this app used to derive "today" from
 * `new Date().toISOString()`, which is **UTC**. Between 00:00 and 05:30 IST the
 * UTC date is still *yesterday*, so on a late shift the whole app defaulted to
 * the wrong day — attendance sheets, invoices, cash-book entries and stock
 * movements all landed on yesterday's date. Every business date now comes from
 * `todayIST()` / `monthIST()`, which format the clock in IST explicitly.
 *
 * Stored timestamps stay canonical (offset-aware ISO, and 24-hour `HH:MM:SS`
 * in the DB because POS rows are `ORDER BY time`) — only the *display* layer
 * converts to 12-hour AM/PM.
 */

export const IST_TZ = "Asia/Kolkata";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Current instant, rendered in IST. */
function istParts(d = new Date()) {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t) => p.find((x) => x.type === t)?.value ?? "";
  // Intl can emit hour "24" for midnight in some ICU builds; normalise to 0.
  let hour = parseInt(get("hour"), 10);
  if (hour === 24) hour = 0;
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour,
    minute: get("minute"),
    second: get("second"),
  };
}

/** Business "today" in Delhi — `YYYY-MM-DD`. */
export function todayIST() {
  const { year, month, day } = istParts();
  return `${year}-${month}-${day}`;
}

/** Business "this month" in Delhi — `YYYY-MM`. */
export function monthIST() {
  const { year, month } = istParts();
  return `${year}-${month}`;
}

/** Previous Delhi month — `YYYY-MM`. Attendance opens here because the
 *  salary being processed is always last month's (owner instruction). */
export function prevMonthIST() {
  const [y, m] = monthIST().split("-").map(Number);
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  return `${py}-${String(pm).padStart(2, "0")}`;
}

/** First day of the current Delhi month — `YYYY-MM-01`. */
export function firstOfMonthIST() {
  return `${monthIST()}-01`;
}

/** N days before today in Delhi — `YYYY-MM-DD`. */
export function daysAgoIST(days) {
  const { year, month, day } = istParts();
  const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  d.setUTCDate(d.getUTCDate() - Number(days));
  return d.toISOString().slice(0, 10);
}

/** `14` → `"2:35 PM"` style suffix pair, given minutes. */
function ampm(hour, minute, second) {
  const h = Number(hour);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const suffix = h < 12 ? "AM" : "PM";
  return `${h12}:${String(minute).padStart(2, "0")}${second ? `:${String(second).padStart(2, "0")}` : ""} ${suffix}`;
}

/**
 * Format a clock time as Indian 12-hour time.
 * Accepts `"14:35"`, `"14:35:07"`, `"2:35 PM"`, a Date, or an ISO timestamp.
 * Returns `""` for empty input so callers can fall back cleanly.
 */
export function fmt12(value, { seconds = false } = {}) {
  if (value === null || value === undefined || value === "") return "";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    const p = istParts(value);
    return ampm(p.hour, p.minute, seconds ? p.second : "");
  }
  const raw = String(value).trim();
  // Already 12-hour — pass through.
  if (/\b(AM|PM)\b/i.test(raw)) return raw;
  // Bare clock time ("14:35:07") — no TZ shift, it is already salon wall-clock.
  const m = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m && Number(m[1]) <= 23) {
    return ampm(m[1], m[2], seconds ? m[3] : "");
  }
  // Full ISO / timestamp — convert into IST.
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    const p = istParts(d);
    return ampm(p.hour, p.minute, seconds ? p.second : "");
  }
  return raw;
}

/** `YYYY-MM-DD` → `04 Oct 2026` (no TZ shift; it is a plain business date). */
export function fmtDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    const p = istParts(d);
    return `${Number(p.day)} ${MONTHS[Number(p.month) - 1]} ${p.year}`;
  }
  return raw;
}

/** Full timestamp → `04 Oct 2026, 02:35 PM` in IST. */
export function fmtDateTime12(value) {
  if (!value) return "";
  const t = fmt12(value);
  return t ? `${fmtDate(value)}, ${t}` : fmtDate(value);
}

/** `YYYY-MM` → `Oct 2026`. */
export function fmtMonth(value) {
  if (!value) return "";
  const m = String(value).trim().match(/^(\d{4})-(\d{2})/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : String(value);
}